import { ChangeEvent, useCallback, useId } from 'react';
import { useLang } from 'hooks/lang';

import { calculateServiceCost, ProjectService } from 'entities';

import {
  Column,
  Row,
  Collapse,
  Tooltip,
  LabeledTextarea,
  LabeledInput,
  Label,
  Alert,
} from 'components/Blocks';
import { MonetaryInput, VariableCostInput } from 'components/Inputs';
import { InfoIcon } from 'components/Icons';

import { useProjectProfile } from '../context';

import { omitUndefined } from 'utils/object';
import { listGet, toTruthyList, toUniqueList } from 'utils/array';

import { CountryService, PhaseProps } from './types';

interface CountryServiceProps extends Pick<PhaseProps, 'dispatch'> {
  service: CountryService;
  disabled?: boolean;
  label: string;
  helpText?: string;
  country: string;
}

function allJustificationsAreEqual(service: CountryService) {
  return (
    toTruthyList(toUniqueList(service.costs.map((x) => x.differenceJustification?.trim())))
      .length === 1
  );
}

function anyCostDiffers(service: CountryService, refCosts: ProjectService['costs'][string]) {
  return service.costs.some(
    ({ id, value }) => !!value && Number(value) !== Number(listGet(refCosts, id)!.value),
  );
}

export function ServiceValues({
  service,
  label,
  disabled,
  helpText,
  dispatch,
  country,
}: CountryServiceProps) {
  const compId = useId();

  const { t } = useLang();

  const {
    project: { services },
  } = useProjectProfile();

  const { costs, outsourceConfig } = service;

  const currentService = listGet(services, service.id)!;

  // Justification was by each variable in the past. Now its per service
  // on this first transition we will simply collect the justification as one and
  // replicate it to all cost variables that have a different value than expected
  const shouldDisplayOneJustification = disabled
    ? allJustificationsAreEqual(service)
    : anyCostDiffers(service, currentService.costs[country]);

  const justification =
    service.costs.find((c) => c.differenceJustification)?.differenceJustification || '';

  const handleCostValueUpdate = useCallback(
    ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => {
      const [index] = name.split('#');

      const existingValue = Number(currentService.costs[country][Number(index)].value);

      const valueIsDifferent = existingValue !== Number(value);

      dispatch({
        servicesActualMetrics: ({ servicesActualMetrics }) =>
          servicesActualMetrics.map((actualService) => {
            if (actualService.id !== service.id) return actualService;

            const updated = {
              ...actualService,
              costs: {
                ...actualService.costs,
                [country]: actualService.costs[country].map((cost, costIndex) =>
                  Number(index) === costIndex
                    ? omitUndefined({
                        ...cost,
                        value,
                        differenceJustification: valueIsDifferent ? justification : undefined,
                      })
                    : cost,
                ),
              },
            };

            if (!updated.outsourceConfig) return updated;

            return {
              ...updated,

              outsourceConfig: {
                ...updated.outsourceConfig,

                clientCost: {
                  ...updated.outsourceConfig?.clientCost,

                  value: calculateServiceCost(updated),
                },
              },
            };
          }),
      });
    },
    [dispatch, service.id, country, justification, currentService.costs],
  );

  const handleJustificationUpdate = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
      dispatch({
        servicesActualMetrics: ({ servicesActualMetrics }) =>
          servicesActualMetrics.map((actualService) =>
            actualService.id === service.id
              ? {
                  ...actualService,
                  costs: {
                    ...actualService.costs,
                    [country]: actualService.costs[country].map((cost) =>
                      typeof cost.differenceJustification === `string`
                        ? {
                            ...cost,
                            differenceJustification: value,
                          }
                        : cost,
                    ),
                  },
                }
              : actualService,
          ),
      });
    },
    [dispatch, service.id, country],
  );

  return (
    <Column gap="6">
      <Row gap="2">
        <Label className="text-base text-primary">{label}</Label>

        {helpText && (
          <Tooltip content={helpText} ensurePresence clickable>
            <InfoIcon size={16} color="primary" />
          </Tooltip>
        )}
      </Row>

      <div className="small_phone::grid-cols-2 grid grid-cols-4 gap-4 small_tablet:grid-cols-1">
        {costs.map(({ type, currency, value, id, name, differenceJustification }, index) => (
          <Column key={`${label}-${id}`} fullWidth={false}>
            <VariableCostInput
              type={type}
              currency={currency || ''}
              currencyDisabled
              disabled={disabled}
              onChange={handleCostValueUpdate}
              // prevents autocompletion
              name={`${index}#value#${compId}`}
              value={value}
              label={name}
              labelClassName="pl-1"
            />

            <Collapse
              onlyRenderIfExpanded
              expanded={
                disabled &&
                !shouldDisplayOneJustification &&
                typeof differenceJustification === 'string'
              }
            >
              <LabeledTextarea label={t('why?')} value={differenceJustification} disabled />
            </Collapse>
          </Column>
        ))}
      </div>

      {shouldDisplayOneJustification && (
        <>
          <LabeledTextarea
            label={t('why-values-differ')}
            value={justification}
            onChange={handleJustificationUpdate}
            disabled={disabled}
          />

          {!disabled && (
            <Alert level="warning">
              {t('be-descriptive-and-clear')}{' '}
              <strong className="block font-semibold">{t('higher-lower-warning')}</strong>
            </Alert>
          )}
        </>
      )}

      {!!outsourceConfig?.approved && (
        <Column>
          <span className="font-medium">{t('outsource-values')}</span>

          <div className="grid grid-cols-3 gap-4 phone:grid-cols-1">
            <LabeledInput
              label={t('provider-name')}
              disabled
              value={outsourceConfig?.providerName || ''}
            />

            <MonetaryInput
              disabled
              label={t('provider-cost')}
              currency={outsourceConfig?.providerCost.currency}
              value={outsourceConfig?.providerCost.value}
            />

            <MonetaryInput
              disabled
              label={t('client-cost')}
              currency={outsourceConfig?.clientCost.currency}
              value={outsourceConfig?.clientCost.value}
            />
          </div>
        </Column>
      )}
    </Column>
  );
}
